import { Search, X } from 'lucide-react'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { useState, useEffect, useRef } from 'react'
import { useDebounce } from '@/utils/hooks'

interface SearchInputProps {
	value: string
	onChange: (value: string) => void
	placeholder?: string
	className?: string
	onClear?: () => void
	debounce?: number
}

export const SearchInput = ({
	value,
	onChange,
	placeholder,
	className = '',
	onClear,
	debounce = 250
}: SearchInputProps) => {
	const [isFocused, setIsFocused] = useState(false)
	const [internalValue, setInternalValue] = useState(value)
	const debouncedValue = useDebounce(internalValue, debounce)
	const inputHasBeenCleared = useRef(false)

	// Update internal value when external value changes
	useEffect(() => {
		setInternalValue(value)
	}, [value])

	// Call onChange when debounced value changes
	useEffect(() => {
		const hasBeenCleared = inputHasBeenCleared.current
		inputHasBeenCleared.current = false
		if (hasBeenCleared) {
			return
		}
		if (debouncedValue !== value) {
			onChange(debouncedValue)
		}
	}, [debouncedValue, onChange, value])

	const handleClear = () => {
		inputHasBeenCleared.current = true
		setInternalValue('')
		onChange('')
		onClear?.()
	}

	return (
		<div className={`flex items-center justify-center ${className}`}>
			<div className='relative w-full max-w-md'>
				<div className='relative'>
					<Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
					<Input
						type='text'
						placeholder={placeholder}
						value={internalValue}
						onChange={e => setInternalValue(e.target.value)}
						onFocus={() => setIsFocused(true)}
						onBlur={() => setIsFocused(false)}
						className={`pl-10 pr-10 ${isFocused ? 'ring-2 ring-primary/20' : ''} ${internalValue ? 'pr-10' : 'pr-4'}`}
					/>
					{internalValue && (
						<Button
							type='button'
							variant='ghost'
							size='icon'
							className='absolute right-1 top-1/2 h-8 w-8 -translate-y-1/2 text-muted-foreground hover:text-foreground'
							onClick={handleClear}
							aria-label='Clear search'
						>
							<X className='h-4 w-4' />
						</Button>
					)}
				</div>
			</div>
		</div>
	)
}
